import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Phone, ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Phone number validation helper
const validatePhoneNumber = (phone: string): { isValid: boolean; formatted: string; error?: string } => {
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Handle US numbers without country code (assume +1)
  if (!cleaned.startsWith('+') && cleaned.length === 10) {
    const usFormatted = `+1${cleaned}`;
    return { isValid: true, formatted: usFormatted };
  }
  
  // Check if it starts with + (international format)
  if (!cleaned.startsWith('+')) {
    return { isValid: false, formatted: cleaned, error: "Phone number must be 10 digits for US numbers or start with + for international" };
  }
  
  // Check minimum length (country code + at least 10 digits)
  if (cleaned.length < 12) {
    return { isValid: false, formatted: cleaned, error: "Phone number is too short" };
  }
  
  // Check maximum length
  if (cleaned.length > 16) {
    return { isValid: false, formatted: cleaned, error: "Phone number is too long" };
  }
  
  // Valid E.164 format
  return { isValid: true, formatted: cleaned };
};

interface CollapsiblePhoneAuthProps {
  onPhoneSubmit: (phoneNumber: string) => void;
  onOTPSubmit: (otp: string) => void;
  disabled?: boolean;
  showOTP?: boolean;
  phoneNumber?: string;
}

export const CollapsiblePhoneAuth = ({ 
  onPhoneSubmit, 
  onOTPSubmit, 
  disabled, 
  showOTP = false,
  phoneNumber = ""
}: CollapsiblePhoneAuthProps) => {
  const [phone, setPhone] = useState(phoneNumber);
  const [otp, setOtp] = useState("");
  const [isOpen, setIsOpen] = useState(showOTP);
  const [phoneError, setPhoneError] = useState<string>("");

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePhoneNumber(phone.trim());
    
    if (!validation.isValid) {
      setPhoneError(validation.error || "Invalid phone number format");
      return;
    }
    
    setPhoneError("");
    onPhoneSubmit(validation.formatted);
  };

  const handleOTPSubmit = (value: string) => {
    setOtp(value);
    if (value.length === 6) {
      onOTPSubmit(value);
    }
  };

  if (showOTP) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Enter the verification code sent to {phone}
          </p>
        </div>
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={otp} onChange={handleOTPSubmit}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-center relative"
          disabled={disabled}
        >
          <div className="flex items-center">
            <Phone className="h-4 w-4 mr-2 text-primary" />
            Continue with Text Message Verification
          </div>
          <div className="absolute right-3">
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-3">
        <form onSubmit={handlePhoneSubmit} className="space-y-3">
          <div className="space-y-2">
            <Input
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError(""); // Clear error when user types
              }}
              disabled={disabled}
              className={`text-center ${phoneError ? 'border-destructive' : ''}`}
            />
            {phoneError && (
              <p className="text-sm text-destructive text-center">{phoneError}</p>
            )}
          </div>
          <Button
            type="submit"
            variant="default"
            className="w-full"
            disabled={disabled || !phone.trim()}
          >
            Send Verification Code
          </Button>
        </form>
      </CollapsibleContent>
    </Collapsible>
  );
};